import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiShield } from 'react-icons/fi';
import { FaFacebook, FaTwitter, FaYoutube } from 'react-icons/fa';

const Footer = () => {
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      window.location.href = `mailto:web@eagleclosing.com?subject=Newsletter Subscription Request&body=Please add my email to the newsletter:%0D%0A%0D%0AEmail: ${encodeURIComponent(email)}`;
      setEmail('');
    }
  };

  return (
    <>
      {/* Footer CTA Bar */}
      <div className="bg-gradient-to-r from-eagle-dark via-eagle-blue to-eagle-deep text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-xl font-semibold mb-1">Ready to get started?</p>
              <p className="text-eagle-sky">We're here to help with your closing.</p>
            </div>
            <Link
              to="/#contact"
              className="group inline-flex items-center space-x-2 bg-white text-eagle-dark px-8 py-3 rounded-full font-semibold hover:bg-eagle-sky hover:text-white transition-all duration-300 shadow-lg"
            >
              <span>Contact Us</span>
              <FiMail className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <footer className="bg-white text-gray-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-eagle-blue rounded-full filter blur-3xl" />
          <div className="absolute top-0 left-0 w-96 h-96 bg-eagle-sky rounded-full filter blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info & Newsletter */}
            <div>
              <Link to="/" className="inline-block mb-6">
                <div className="relative">
                  <img
                    src="/logo.webp"
                    alt="Eagle Closing"
                    className="h-14 w-auto"
                  />
                  <div className="absolute inset-0 bg-eagle-sky/20 rounded-full blur-xl" />
                </div>
              </Link>
              <p className="font-sans text-gray-600 mb-6 leading-relaxed">
                Successful real estate closings rely on the support of a dependable title company.
                Make sure yours is up to the task!
              </p>

              {/* Trust Badge */}
              <div className="inline-flex items-center space-x-2 bg-eagle-blue/10 border border-eagle-blue/20 rounded-full px-4 py-2 mb-6">
                <FiShield className="h-4 w-4 text-eagle-blue" />
                <span className="text-sm text-eagle-blue">Licensed & Insured</span>
              </div>

              <form onSubmit={handleSubscribe} className="space-y-3">
                <label className="text-sm font-semibold text-eagle-dark">Newsletter</label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-eagle-sky focus:border-eagle-sky outline-none bg-gray-50"
                  />
                  <FiMail className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-eagle-sky" />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-eagle-sky to-eagle-light hover:from-eagle-blue hover:to-eagle-sky text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Subscribe
                </button>
              </form>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-display text-lg font-semibold text-eagle-dark mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {[
                  { name: 'Home', path: '/' },
                  { name: 'About Us', path: '/about-us' },
                  { name: 'Services', path: '/services' },
                  { name: 'Blog', path: '/blog' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="font-sans text-gray-600 hover:text-eagle-blue hover:translate-x-1 inline-block transition-all duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Pages */}
            <div>
              <h3 className="font-display text-lg font-semibold text-eagle-dark mb-6">Legal</h3>
              <ul className="space-y-3">
                {[
                  { name: 'Privacy Policy', path: '/privacy-policy' },
                  { name: 'Terms of Use', path: '/terms-of-use' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="font-sans text-gray-600 hover:text-eagle-blue hover:translate-x-1 inline-block transition-all duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-display text-lg font-semibold text-eagle-dark mb-6">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-eagle-blue/10 rounded-lg flex items-center justify-center">
                    <FiMapPin className="h-5 w-5 text-eagle-blue" />
                  </div>
                  <span className="font-sans text-gray-600 text-sm leading-relaxed pt-1">
                    1100 West Valley Road, Suite 8<br />Wayne, PA 19087
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-eagle-blue/10 rounded-lg flex items-center justify-center">
                    <FiMail className="h-5 w-5 text-eagle-blue" />
                  </div>
                  <a
                    href="mailto:web@eagleclosing.com"
                    className="font-sans text-gray-600 hover:text-eagle-blue transition-colors"
                  >
                    web@eagleclosing.com
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-eagle-blue/10 rounded-lg flex items-center justify-center">
                    <FiPhone className="h-5 w-5 text-eagle-blue" />
                  </div>
                  <a
                    href="tel:4845808865"
                    className="font-sans text-gray-600 hover:text-eagle-blue transition-colors"
                  >
                    484-580-8865
                  </a>
                </li>
                <li className="flex items-center space-x-3 pt-2">
                  {[
                    { icon: FaFacebook, href: 'https://facebook.com', label: 'Facebook' },
                    { icon: FaTwitter, href: 'https://twitter.com', label: 'Twitter' },
                    { icon: FaYoutube, href: 'https://youtube.com', label: 'YouTube' },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gradient-to-br from-eagle-sky to-eagle-light rounded-full flex items-center justify-center text-white hover:from-eagle-blue hover:to-eagle-sky transition-all duration-300 shadow-md hover:shadow-lg"
                      aria-label={social.label}
                    >
                      <social.icon className="h-5 w-5" />
                    </a>
                  ))}
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright Bar */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-center font-sans text-sm text-gray-500">
              © {new Date().getFullYear()} Eagle Closing Services. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
